import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs, orderBy, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../admin/firebase/config';
import { sanitizeName, sanitizePhone, sanitizeEmail, sanitizeAddress } from '../utils/sanitization';
import whatsappOtpService from '../services/whatsappOtpService';
import logger from '../utils/logger';

const CustomerAuthContext = createContext();

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);

  // Fetch order history by phone number
  const fetchOrderHistory = useCallback(async (phone) => {
    try {
      const q = query(
        collection(db, 'orders'),
        where('customer.phone', '==', phone),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(q);
      const orderList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setOrders(orderList);
      return orderList;
    } catch (error) {
      logger.error('Error fetching order history:', error);
      return [];
    }
  }, []);

  // Fetch customer profile from Firestore
  const fetchCustomerProfile = useCallback(async (uid) => {
    try {
      const customerDoc = await getDoc(doc(db, 'customer_accounts', uid));

      if (customerDoc.exists()) {
        const profile = customerDoc.data();
        setCustomerProfile(profile);
        setFavorites(profile.favorites || []);
        setSavedAddresses(profile.savedAddresses || []);

        // Fetch order history
        await fetchOrderHistory(profile.phone);
      }
    } catch (error) {
      logger.error('Error fetching customer profile:', error);
    }
  }, [fetchOrderHistory]);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // Fetch customer profile from Firestore
        await fetchCustomerProfile(user.uid);
      } else {
        setCustomerProfile(null);
        setOrders([]);
        setFavorites([]);
        setSavedAddresses([]);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, [fetchCustomerProfile]);

  // Sign up with email and password
  const signUp = async (email, password, name, phone) => {
    try {
      // Sanitize inputs to prevent XSS attacks
      const sanitizedEmail = sanitizeEmail(email);
      const sanitizedName = sanitizeName(name);
      const sanitizedPhone = sanitizePhone(phone);

      const userCredential = await createUserWithEmailAndPassword(auth, sanitizedEmail, password);
      const user = userCredential.user;

      // Update display name
      await updateProfile(user, { displayName: sanitizedName });

      // Create customer profile in Firestore
      await setDoc(doc(db, 'customer_accounts', user.uid), {
        uid: user.uid,
        email: sanitizedEmail,
        name: sanitizedName,
        phone: sanitizedPhone,
        createdAt: serverTimestamp(),
        favorites: [],
        savedAddresses: [],
        totalOrders: 0,
        totalSpent: 0
      });

      await fetchCustomerProfile(user.uid);

      return { success: true, user };
    } catch (error) {
      console.error('Sign up error:', error);
      return { success: false, error: getAuthErrorMessage(error.code) };
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await fetchCustomerProfile(userCredential.user.uid);
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('Sign in error:', error);
      return { success: false, error: getAuthErrorMessage(error.code) };
    }
  };

  // Sign out
  const logout = async () => {
    try {
      await signOut(auth);
      setCustomerProfile(null);
      setOrders([]);
      setFavorites([]);
      setSavedAddresses([]);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: getAuthErrorMessage(error.code) };
    }
  };

  // Add to favorites
  const addToFavorites = async (item) => {
    if (!currentUser) return { success: false, error: 'Please login first' };

    try {
      const newFavorites = [...favorites, {
        name: item.name,
        price: item.price,
        isVeg: item.isVeg,
        image: item.image || null,
        category: item.category || '',
        addedAt: new Date().toISOString()
      }];

      await updateDoc(doc(db, 'customer_accounts', currentUser.uid), {
        favorites: newFavorites
      });

      setFavorites(newFavorites);
      return { success: true };
    } catch (error) {
      console.error('Add to favorites error:', error);
      return { success: false, error: error.message };
    }
  };

  // Remove from favorites
  const removeFromFavorites = async (itemName) => {
    if (!currentUser) return { success: false, error: 'Please login first' };

    try {
      const newFavorites = favorites.filter(fav => fav.name !== itemName);

      await updateDoc(doc(db, 'customer_accounts', currentUser.uid), {
        favorites: newFavorites
      });

      setFavorites(newFavorites);
      return { success: true };
    } catch (error) {
      console.error('Remove from favorites error:', error);
      return { success: false, error: error.message };
    }
  };

  // Check if item is in favorites
  const isFavorite = (itemName) => {
    return favorites.some(fav => fav.name === itemName);
  };

  // Add saved address
  const addSavedAddress = async (address) => {
    if (!currentUser) return { success: false, error: 'Please login first' };

    try {
      const newAddress = {
        id: Date.now().toString(),
        label: sanitizeName(address.label || 'Home'),
        building: sanitizeAddress(address.building || ''),
        address: sanitizeAddress(address.address || ''),
        location: sanitizeAddress(address.location || ''),
        latitude: typeof address.latitude === 'number' ? address.latitude : null,
        longitude: typeof address.longitude === 'number' ? address.longitude : null,
        isDefault: savedAddresses.length === 0,
        createdAt: new Date().toISOString()
      };

      const newAddresses = [...savedAddresses, newAddress];

      await updateDoc(doc(db, 'customer_accounts', currentUser.uid), {
        savedAddresses: newAddresses
      });

      setSavedAddresses(newAddresses);
      return { success: true, address: newAddress };
    } catch (error) {
      console.error('Add saved address error:', error);
      return { success: false, error: error.message };
    }
  };

  // Remove saved address
  const removeSavedAddress = async (addressId) => {
    if (!currentUser) return { success: false, error: 'Please login first' };

    try {
      const newAddresses = savedAddresses.filter(addr => addr.id !== addressId);

      await updateDoc(doc(db, 'customer_accounts', currentUser.uid), {
        savedAddresses: newAddresses
      });

      setSavedAddresses(newAddresses);
      return { success: true };
    } catch (error) {
      console.error('Remove saved address error:', error);
      return { success: false, error: error.message };
    }
  };

  // Set default address
  const setDefaultAddress = async (addressId) => {
    if (!currentUser) return { success: false, error: 'Please login first' };

    try {
      const newAddresses = savedAddresses.map(addr => ({
        ...addr,
        isDefault: addr.id === addressId
      }));

      await updateDoc(doc(db, 'customer_accounts', currentUser.uid), {
        savedAddresses: newAddresses
      });

      setSavedAddresses(newAddresses);
      return { success: true };
    } catch (error) {
      console.error('Set default address error:', error);
      return { success: false, error: error.message };
    }
  };

  // Update customer profile
  const updateCustomerProfile = async (updates) => {
    if (!currentUser) return { success: false, error: 'Please login first' };

    try {
      await updateDoc(doc(db, 'customer_accounts', currentUser.uid), updates);
      setCustomerProfile(prev => ({ ...prev, ...updates }));
      return { success: true };
    } catch (error) {
      console.error('Update profile error:', error);
      return { success: false, error: error.message };
    }
  };

  // Helper function to get user-friendly auth error messages
  const getAuthErrorMessage = (code) => {
    switch (code) {
      case 'auth/email-already-in-use':
        return 'This email is already registered. Please sign in.';
      case 'auth/invalid-email':
        return 'Invalid email address.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      case 'auth/invalid-credential':
        return 'Invalid email or password.';
      default:
        return 'An error occurred. Please try again.';
    }
  };

  const value = {
    currentUser,
    customerProfile,
    loading,
    orders,
    favorites,
    savedAddresses,
    signUp,
    signIn,
    logout,
    resetPassword,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    addSavedAddress,
    removeSavedAddress,
    setDefaultAddress,
    updateCustomerProfile,
    fetchOrderHistory
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export default CustomerAuthContext;
